import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { CereriService } from '../cereri.service';
import { Observable, finalize, map } from 'rxjs';
import { helper } from 'src/environments/helper'
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/angular-editor/editorConfig';
import { CommonService } from 'src/app/services/common.service';
import { countiesList } from 'src/app/shared/county-list';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CererePriorityService } from 'src/app/platform/nomenclatoare/cerere-priority/cerere-priority.service';
import { ToastrService } from 'ngx-toastr';
import { UsersByCompartimentePayload } from '../cereri.interface';

@Component({
    selector: 'app-add-cerere',
    templateUrl: './add-cerere.component.html',
    styleUrls: ['./add-cerere.component.scss']
})
export class AddCerereComponent implements OnInit {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    // general success alert
    successTitle: string = environment.config.customNotifications.headers.successForm;
    successIcon: string = environment.config.customNotifications.icons.success;
    successType: string = environment.config.customNotifications.icons.success;

    helper = helper;

    editorConfig!: AngularEditorConfig;
    judete: any = countiesList;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        id_cerere_type: new FormControl(''),
        id_departament: new FormControl(''),
        subject: new FormControl(''),
        description: new FormControl(''),
        id_cerere_status: new FormControl(''),
        id_cerere_priority: new FormControl(''),
        id_user_assigned: new FormControl(''),
        check_cetatean: new FormControl(''),
        cet_denumire: new FormControl(''),
        cet_cui: new FormControl(''),
        cet_nume: new FormControl(''),
        cet_prenume: new FormControl(''),
        cet_cnp: new FormControl(''),
        cet_ci_serie: new FormControl(''),
        cet_ci_numar: new FormControl(''),
        cet_telefon: new FormControl(''),
        cet_email: new FormControl(''),
        cet_adr_judet: new FormControl(''),
        cet_adr_oras: new FormControl(''),
        cet_adr_sector: new FormControl(''),
        cet_adr_strada: new FormControl(''),
        cet_adr_numar: new FormControl(''),
        cet_adr_bloc: new FormControl(''),
        cet_adr_scara: new FormControl(''),
        cet_adr_etaj: new FormControl(''),
        cet_adr_apartament: new FormControl(''),
        cet_adr_observatii: new FormControl(''),
        files: new FormControl(''),
    });

    departamente$: any[] = [];
    tipuri$!: Observable<Generic[]>;
    usersActive: any[] = [];
    requestPriorities$!: Observable<Generic[]>;

    uploading: boolean[] = [];

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    constructor(
        private dialogRef: MatDialogRef<AddCerereComponent>,
        private cereriService: CereriService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private cererePriorityService: CererePriorityService,
        public toastr: ToastrService,
        private fb: FormBuilder,
        private commonService: CommonService,
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.loadRequestPriorities();
        this.initForm();
        this.loadDepartamente();
    }

    initForm() {
        this.form = this.fb.group({
            id_cerere_type: ['', [Validators.required]],
            id_departament: ['', [Validators.required]],
            subject: ['', [Validators.required]],
            description: ['', [Validators.required]],
            id_cerere_status: [1],
            id_cerere_priority: ['', [Validators.required]],
            id_user_assigned: [''],
            check_cetatean: [''],
            cet_denumire: [''],
            cet_cui: [''],
            cet_nume: [''],
            cet_prenume: [''],
            cet_cnp: [''],
            cet_ci_serie: [''],
            cet_ci_numar: [''],
            cet_telefon: [''],
            cet_email: [''],
            cet_adr_judet: [-1],
            cet_adr_oras: [''],
            cet_adr_sector: [-1],
            cet_adr_strada: [''],
            cet_adr_numar: [''],
            cet_adr_bloc: [''],
            cet_adr_scara: [''],
            cet_adr_etaj: [''],
            cet_adr_apartament: [''],
            cet_adr_observatii: [''],
            files: [''],
        })
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.cereriService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const filesToSend = this.form.get('files')?.value || [];
        let formData = new FormData();

        Object.keys(this.form.value).forEach(key => {
            if (key !== 'files') {
                const value = this.form.value[key];
                if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            }
        })

        filesToSend.forEach((file: any, index: any) => {
            formData.append(`files-${index}`, file);
        });

        this.cereriService
            .addRequest(formData)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: () => {
                    let errorMessage = environment.config.customNotifications.generalMessages.error;
                    this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                },
            });
    }

    loadRequestPriorities() {
        this.isLoading = true;
        this.requestPriorities$ = this.cererePriorityService.getCererePriorities().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    loadDepartamente() {
        this.isLoading = true;
        this.cereriService.getCompartimente()
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    let response = (typeof res.status_code !== 'undefined' ? res : res.error)
                    if (typeof response.status_code !== 'undefined') {
                        if (response.status_code == 200 && typeof response.data !== 'undefined') {
                            this.departamente$ = response.data;
                            this.isLoading = false;
                            return;
                        }
                    } else {
                        let errorMessage = environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(environment.config.customNotifications.headers.error, errorMessage, environment.config.customNotifications.icons.error);
                    }
                },
                error: (res: any) => {
                    console.log(res.error);
                }
            })
    }

    onDepartamentChange(departamentId: number): void {
        if (departamentId) {
            this.loadTipuri(departamentId);
            this.loadUsers(departamentId);
        } else {
            this.tipuri$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            })
        }
    }

    loadTipuri(departamentId?: number) {
        this.isLoading = true;

        let tipuriObservable$ = departamentId
            ? this.cereriService.getTipuriByDepartamenteId(departamentId)
            : this.commonService.getCompartimenteActive();

        this.tipuri$ = tipuriObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    loadUsers(departamentId: number) {
        let payload: UsersByCompartimentePayload = { compartiment_id: departamentId };

        let usersObservable$ = departamentId
            ? this.cereriService.getActiveUsersByCompartiment(payload)
            : this.commonService.getUsersActive();

        usersObservable$
            .pipe(map((res: any) => [this.defaultParameter, ...res]))
            .subscribe((data) => {
                this.usersActive = data;
                this.isLoading = false;
            });
    }

    onDepartamentTipChange(tipId: number): void { }

    addFile(event: any) {
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            const currentFiles = this.form.get('files')?.value || [];
            currentFiles.push(file);
            this.form.get('files')?.setValue(currentFiles);

            this.uploading.push(true);

            setTimeout(() => {
                this.uploading.fill(false);

                this.openToast({
                    title: this.successTitle,
                    message: 'Fișierul dvs. a fost adăugat cu succes.',
                    type: this.successIcon
                });
            }, 1000);
        }
    }

    removeFile(index: number) {
        const currentFiles = this.form.get('files')?.value || [];
        currentFiles.splice(index, 1);
        this.uploading.splice(index, 1);
        this.form.get('files')?.setValue(currentFiles);
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(message, title, options, options.iconClasses[type]);
    }
}
